/**
 * Turning ISF INPUTS into the shader-uniform shape the rest of the app already
 * speaks.
 *
 * This is the hinge of the whole integration. `parseShaderUniforms` produces
 * `ShaderUniformInfo`, and every downstream consumer — the knob UI, MIDI
 * mapping, System Link, the AI uniform analysis, feed cards, knob persistence —
 * is built on it. If the ISF path emits the same structure with the same
 * ordering, all of that keeps working with no changes at all.
 *
 * The CC rule is Shadygram's, deliberately: `mappableUniforms[i]` binds CC
 * *i*+1. Shadygram's numbering is load-bearing on saved `knob_values` in every
 * conversation and feed row, so it is the device that adapts, not the web app.
 */

import type { ShaderUniform, ShaderUniformInfo } from '@/utils/shaderParser';

import type { ISFDoc, ISFInput } from './types';
import { HOST_DRIVEN_INPUT_NAMES, HOST_DRIVEN_INPUT_TYPES } from './types';

const AXES = ['x', 'y', 'z', 'w'] as const;

/** An input the host feeds every frame, so it never gets a knob or a CC slot. */
export function isHostDrivenInput(input: ISFInput): boolean {
  return (
    HOST_DRIVEN_INPUT_TYPES.includes(input.TYPE) ||
    HOST_DRIVEN_INPUT_NAMES.includes(input.NAME)
  );
}

/** Image-like inputs, which are bound as samplers rather than knobs. */
export function samplerInputs(doc: ISFDoc): ISFInput[] {
  return (doc.INPUTS ?? []).filter((i) => HOST_DRIVEN_INPUT_TYPES.includes(i.TYPE));
}

function numberAt(value: ISFInput['DEFAULT'], index: number, fallback: number): number {
  if (Array.isArray(value)) {
    const n = value[index];
    return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  return fallback;
}

function scalarRange(input: ISFInput, index: number): [number, number] {
  const min = numberAt(input.MIN, index, 0);
  const max = numberAt(input.MAX, index, 1);
  // A degenerate range would make every knob position map to the same value.
  return min < max ? [min, max] : [min, min + 1];
}

/** The GLSL type an ISF input declares. */
export function glslTypeFor(type: ISFInput['TYPE']): string {
  switch (type) {
    case 'bool':
    case 'event':
      return 'bool';
    case 'long':
      return 'int';
    case 'color':
      return 'vec4';
    case 'point2D':
      return 'vec2';
    case 'image':
    case 'audio':
    case 'audioFFT':
      return 'sampler2D';
    default:
      return 'float';
  }
}

/** ISF's own scalar read (VIDOS ISFParser ReadScalar): a finite number, or a boolean as 1/0; else undefined. */
function engineScalar(value: unknown): number | undefined {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** The numeric members of an array (VIDOS ISFParser NumberArrayMember); empty for anything else. */
function engineNumbers(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((n): n is number => typeof n === 'number' && Number.isFinite(n)) : [];
}

/** The engine's parsed value of an input with no DEFAULT a number reads from (VIDOS ISFInput default_value). */
const ENGINE_UNSET_DEFAULT = 0.5;
/** Below this, a declared range is a single value (VIDOS NormalisedDefault). */
const DEGENERATE_SPAN = 1e-6;

/**
 * Where knob `component` of `input` starts, 0..1: its source default (PROTOCOL.md §3.2). The engine's rule exactly
 * (VIDOS ISFParser.cpp NormalisedDefault over its own parse, which every vidOS device and shadygram's engine seeds
 * the knobs with: Scene::SeedShaderParameterDefaults), so a panel shows where the engine has the knob. An event
 * starts off; a bool at its DEFAULT; a menu (a `long` with VALUES) mid-step on its DEFAULT's entry (0 when that is not
 * one); anything else its DEFAULT through MIN..MAX (a reversed pair read in order; a single-value range: 0), clamped.
 * No DEFAULT: the engine's fallback (a number 0.5, a colour component 0 and its alpha 1). Held to
 * shared/systemLink/golden/sourcedefault.json with the engine.
 */
export function isfKnobDefault(input: ISFInput, component = 0): number {
  if (input.TYPE === 'event') return 0;
  if (input.TYPE === 'bool') return (engineScalar(input.DEFAULT) ?? 0) >= 0.5 ? 1 : 0;
  const scalar = engineScalar(input.DEFAULT) ?? ENGINE_UNSET_DEFAULT;
  const values = input.TYPE === 'long' ? engineNumbers(input.VALUES).map(Math.trunc) : [];
  if (values.length > 0) {
    const entry = values.indexOf(Math.trunc(scalar));
    return entry >= 0 ? (entry + 0.5) / values.length : 0;
  }

  const vector = input.TYPE === 'point2D' || input.TYPE === 'color';
  let value = scalar;
  if (input.TYPE === 'point2D') {
    const point = engineNumbers(input.DEFAULT);
    value = component < point.length ? point[component] : ENGINE_UNSET_DEFAULT;
  } else if (input.TYPE === 'color') {
    const color = engineNumbers(input.DEFAULT);
    value = component < color.length ? color[component] : component === 3 ? 1 : 0;
  }
  let min = vector ? engineNumbers(input.MIN)[component] ?? 0 : engineScalar(input.MIN) ?? 0;
  let max = vector ? engineNumbers(input.MAX)[component] ?? 1 : engineScalar(input.MAX) ?? 1;
  if (min > max) [min, max] = [max, min];
  const span = max - min;
  if (Math.abs(span) < DEGENERATE_SPAN) return 0;
  return Math.min(1, Math.max(0, (value - min) / span));
}

/** Knob slots an input consumes, matching the canonical CC rule. */
export function slotCount(input: ISFInput): number {
  if (isHostDrivenInput(input)) return 0;
  if (input.TYPE === 'point2D') return 2;
  if (input.TYPE === 'color') return 4;
  return 1;
}

function componentsFor(input: ISFInput, count: number, group: 'color' | 'point'): ShaderUniform[] {
  const label = input.LABEL ?? input.NAME;
  return Array.from({ length: count }, (_, i): ShaderUniform => ({
    name: `${input.NAME}_${AXES[i]}`,
    type: 'float',
    line: 1,
    isCustom: true,
    defaultValue: numberAt(input.DEFAULT, i, group === 'color' && i === 3 ? 1 : 0.5),
    knobDefault: isfKnobDefault(input, i),
    range: scalarRange(input, i),
    label: `${label} ${AXES[i].toUpperCase()}`,
    isfType: input.TYPE,
    ...(group === 'color' ? { colorGroupBase: input.NAME } : { pointGroupBase: input.NAME }),
  }));
}

/** One ISF input → the knob-level uniforms it contributes. */
export function uniformsForInput(input: ISFInput): ShaderUniform[] {
  if (isHostDrivenInput(input)) return [];

  const label = input.LABEL ?? input.NAME;

  if (input.TYPE === 'color') return componentsFor(input, 4, 'color');
  if (input.TYPE === 'point2D') return componentsFor(input, 2, 'point');

  const base: ShaderUniform = {
    name: input.NAME,
    type: 'float',
    line: 1,
    isCustom: true,
    defaultValue: numberAt(input.DEFAULT, 0, 0.5),
    knobDefault: isfKnobDefault(input),
    range: scalarRange(input, 0),
    label,
    isfType: input.TYPE,
  };

  if (typeof input.IDENTITY === 'number') base.identity = input.IDENTITY;

  if (input.TYPE === 'bool' || input.TYPE === 'event') {
    return [{
      ...base,
      type: 'bool',
      defaultValue: input.DEFAULT === true || numberAt(input.DEFAULT, 0, 0) >= 0.5 ? 1 : 0,
      range: [0, 1],
    }];
  }

  if (input.TYPE === 'long') {
    const values = input.VALUES?.length ? input.VALUES : undefined;
    return [{
      ...base,
      type: 'int',
      values,
      labels: input.LABELS,
      // With VALUES present the knob indexes the menu; otherwise it is a plain
      // integer slider over MIN..MAX.
      range: values ? [0, Math.max(0, values.length - 1)] : scalarRange(input, 0),
      defaultValue: numberAt(input.DEFAULT, 0, values ? values[0] : 0),
    }];
  }

  return [base];
}

/**
 * Build the shared IR for an ISF document.
 *
 * `mappableUniforms` order *is* the CC assignment, so it must follow INPUTS
 * declaration order exactly — reordering would silently re-map saved presets.
 */
export function isfInputsToUniformInfo(doc: ISFDoc): ShaderUniformInfo {
  const uniforms: ShaderUniform[] = [];
  for (const input of doc.INPUTS ?? []) uniforms.push(...uniformsForInput(input));

  return {
    uniforms,
    customUniforms: uniforms,
    totalUniforms: uniforms.length,
    mappableUniforms: uniforms,
  };
}

/** A finite knob position clamped to 0..1, or null for anything else. */
function knobPosition(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : null;
}

/**
 * CC-keyed knob positions → keyed by knob-level uniform name, using the CC
 * rule above (CC *n* drives `mappableUniforms[n-1]`). Names survive INPUTS
 * being reordered or inserted, which CC numbers don't, so they are what
 * travels between peers that may momentarily run different code. CCs with no
 * uniform are dropped.
 */
export function knobValuesByName(
  ccValues: Readonly<Record<number, number>>,
  info: Pick<ShaderUniformInfo, 'mappableUniforms'>,
): Record<string, number> {
  const out: Record<string, number> = {};
  info.mappableUniforms.forEach((uniform, i) => {
    const cc = i + 1;
    if (!Object.prototype.hasOwnProperty.call(ccValues, cc)) return;
    const value = knobPosition(ccValues[cc]);
    if (value !== null) out[uniform.name] = value;
  });
  return out;
}

/**
 * Inverse of {@link knobValuesByName} against *this* client's uniform list.
 * Only names the shader declares are read (never arbitrary keys of the
 * incoming object), so a hostile or stale map can't inject CCs.
 */
export function knobValuesByCc(
  namedValues: Readonly<Record<string, number>>,
  info: Pick<ShaderUniformInfo, 'mappableUniforms'>,
): Record<number, number> {
  const out: Record<number, number> = {};
  info.mappableUniforms.forEach((uniform, i) => {
    if (!Object.prototype.hasOwnProperty.call(namedValues, uniform.name)) return;
    const value = knobPosition(namedValues[uniform.name]);
    if (value !== null) out[i + 1] = value;
  });
  return out;
}
